import { ReactNode, useState } from 'react'
import {
  Flex,
  HStack,
  Text,
  Button,
  Menu,
  MenuButton,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react'
import { FiSun, FiChevronDown, FiClock, FiCheckCircle, FiGrid } from 'react-icons/fi'
import { Folder, FolderScope } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'
import SmartRenamingModal from '@/components/Modals/SmartRenamingModal'
import SmartOrganizeModal from '@/components/Modals/SmartOrganizeModal'
import { useNavigation } from '@/hooks/useNavigation'
import { useAiActionScope } from '@/hooks/useAiActionScope'
import { useSmartRenameStatus } from '@/hooks/useSmartRenameStatus'
import { useSmartOrganizeStatus } from '@/hooks/useSmartOrganizeStatus'

interface HeaderActionsProps {
  /** The folder these actions act on; null is the root ("All Documents") scope. */
  folder: Folder | null
  onFilesChange?: () => void
  onFolderTreeChange?: () => void
  /** The <MenuItem>s for the Actions dropdown — the only part that differs per header. */
  menuItems: ReactNode
}

/**
 * The two AI actions plus the Actions menu, shared by the folder and root headers.
 *
 * Both headers offer the same pair of long-running AI workflows over different scopes, so the
 * status polling, scope freezing and modals all live here rather than being duplicated.
 */
export default function HeaderActions({
  folder,
  onFilesChange,
  onFolderTreeChange,
  menuItems,
}: HeaderActionsProps) {
  const { t } = useAppTranslation(['common', 'files'])
  // Scope is frozen when a modal opens: these workflows are long-lived (2s polling, multi-minute
  // jobs) while the explorer selection behind the overlay can be cleared by navigation.
  const [renameScopeIds, setRenameScopeIds] = useState<number[]>([])
  const [organizeScopeIds, setOrganizeScopeIds] = useState<number[]>([])
  // How many of the user's selected files were left out for still being processed. Shown
  // in the modal rather than a toast: these jobs run for minutes, and a toast would be
  // long gone by the time the results are reviewed.
  const [renameSkippedCount, setRenameSkippedCount] = useState(0)
  const [organizeSkippedCount, setOrganizeSkippedCount] = useState(0)
  const {
    isOpen: isSmartRenameModalOpen,
    onOpen: onSmartRenameModalOpen,
    onClose: onSmartRenameModalClose,
  } = useDisclosure()
  const {
    isOpen: isSmartOrganizeModalOpen,
    onOpen: onSmartOrganizeModalOpen,
    onClose: onSmartOrganizeModalClose,
  } = useDisclosure()
  const readyScopeFor = useAiActionScope()
  const { selectedFiles, folderFiles } = useNavigation()
  const scope: FolderScope = folder ? folder.id : 'root'
  const { activeJob, hasActiveJob, hasCompletedJob, checkStatus } = useSmartRenameStatus(scope)
  const {
    activeJob: organizeActiveJob,
    hasActiveJob: hasOrganizeActiveJob,
    hasCompletedJob: hasOrganizeCompletedJob,
    checkStatus: checkOrganizeStatus,
  } = useSmartOrganizeStatus(scope)

  const handleSmartRenameOpen = () => {
    // An empty filtered scope must not fall through to an empty array: the modal reads
    // that as "not scoped" and sends no file_ids, which the server takes as the entire
    // folder — silently widening a 3-file selection to everything.
    const readyScope = readyScopeFor('smartRename')
    if (!readyScope) return

    checkStatus() // Refresh status before opening modal
    setRenameScopeIds(readyScope.ids)
    setRenameSkippedCount(readyScope.skipped)
    onSmartRenameModalOpen()
  }

  const handleSmartOrganizeOpen = () => {
    const readyScope = readyScopeFor('smartOrganize')
    if (!readyScope) return

    checkOrganizeStatus()
    setOrganizeScopeIds(readyScope.ids)
    setOrganizeSkippedCount(readyScope.skipped)
    onSmartOrganizeModalOpen()
  }

  const handleRenamingComplete = () => {
    onFilesChange?.()
    checkStatus() // Refresh status after renaming
  }

  const handleOrganizeComplete = () => {
    // Organizing files files them into folders, so the tree changes too.
    onFolderTreeChange?.()
    onFilesChange?.()
    checkOrganizeStatus()
  }

  return (
    <>
      <Flex
        gap={2}
        wrap="wrap"
        flexShrink={0}
        // Below `lg` the three labelled buttons and the folder name cannot share a line, so the
        // group takes the full width and flex-wrap drops it below the title. `wrap` on the group
        // itself keeps the longer Spanish labels from overflowing on the narrowest phones.
        basis={{ base: '100%', lg: 'auto' }}
      >
        <Button
          size="sm"
          variant="outline"
          borderRadius="lg"
          fontWeight="500"
          onClick={handleSmartRenameOpen}
          colorScheme={hasActiveJob ? 'blue' : hasCompletedJob ? 'green' : 'purple'}
          leftIcon={hasActiveJob ? <FiClock /> : hasCompletedJob ? <FiCheckCircle /> : <FiSun />}
        >
          <HStack spacing={1.5}>
            <Text>{t('smartRename.tileTitle', { ns: 'files' })}</Text>
            {selectedFiles.size > 0 && (
              <Text fontSize="xs" color="gray.500" fontWeight="500">
                {selectedFiles.size}
              </Text>
            )}
            {hasActiveJob && (
              <Text fontSize="xs" color="blue.500" fontWeight="500">
                {activeJob?.progress}%
              </Text>
            )}
            {hasCompletedJob && !hasActiveJob && (
              <Text fontSize="xs" color="green.500" fontWeight="500">
                {t('smartRename.ready', { ns: 'files' })}
              </Text>
            )}
          </HStack>
        </Button>

        <Button
          size="sm"
          variant="outline"
          borderRadius="lg"
          fontWeight="500"
          onClick={handleSmartOrganizeOpen}
          colorScheme={hasOrganizeActiveJob ? 'blue' : hasOrganizeCompletedJob ? 'green' : 'teal'}
          leftIcon={
            hasOrganizeActiveJob ? (
              <FiClock />
            ) : hasOrganizeCompletedJob ? (
              <FiCheckCircle />
            ) : (
              <FiGrid />
            )
          }
        >
          <HStack spacing={1.5}>
            <Text>{t('smartOrganize.tileTitle', { ns: 'files' })}</Text>
            {selectedFiles.size > 0 && (
              <Text fontSize="xs" color="gray.500" fontWeight="500">
                {selectedFiles.size}
              </Text>
            )}
            {hasOrganizeActiveJob && (
              <Text fontSize="xs" color="blue.500" fontWeight="500">
                {organizeActiveJob?.progress}%
              </Text>
            )}
            {hasOrganizeCompletedJob && !hasOrganizeActiveJob && (
              <Text fontSize="xs" color="green.500" fontWeight="500">
                {t('smartOrganize.ready', { ns: 'files' })}
              </Text>
            )}
          </HStack>
        </Button>

        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<FiChevronDown />}
            colorScheme="brand"
            size="sm"
            borderRadius="lg"
            fontWeight="500"
          >
            {t('buttons.actions', { ns: 'common' })}
          </MenuButton>
          <MenuList>{menuItems}</MenuList>
        </Menu>
      </Flex>

      {/* Smart Renaming Modal */}
      <SmartRenamingModal
        isOpen={isSmartRenameModalOpen}
        onClose={onSmartRenameModalClose}
        folder={folder}
        selectedFileIds={renameScopeIds}
        skippedCount={renameSkippedCount}
        folderFileCount={folderFiles.length}
        onRenamingComplete={handleRenamingComplete}
      />

      {/* Smart Organize Modal */}
      <SmartOrganizeModal
        isOpen={isSmartOrganizeModalOpen}
        onClose={onSmartOrganizeModalClose}
        folder={folder}
        selectedFileIds={organizeScopeIds}
        skippedCount={organizeSkippedCount}
        folderFileCount={folderFiles.length}
        onOrganizeComplete={handleOrganizeComplete}
      />
    </>
  )
}
